import React from 'react'
import { PauseIcon } from '@commons/icons/pause-icon/PauseIcon'
import { PlayIcon } from '@commons/icons/play-icon/PlayIcon'
import { ButtonProps, ButtonRefs } from '@nespresso-design-system/components'
import { useRef, useState, useEffect } from 'preact/hooks'
import './Video.scss'
import ExpandableButton from '@commons/expandable-button/ExpandableButton'
import viewportHelper from '@content/helpers/src/viewport.helpers.ts'
import { getTestId } from '@content/helpers/src/utils'

type VideoProps = {
    url: string
    urlMobile?: string | null
    button?: ButtonProps
    auto_play?: boolean
    a11y_video_description?: string
    hasModal?: boolean
    onVideoClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
    className?: string
    showControls?: boolean
}

const Video = ({
    url,
    urlMobile = null,
    button = null,
    auto_play = false,
    hasModal = false,
    onVideoClick,
    className = '',
    a11y_video_description,
    showControls = true
}: VideoProps) => {
    const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
    ).matches

    const [isPlaying, setIsPlaying] = useState(auto_play && !reducedMotion)
    const videoRef = useRef(null)
    const ctaRef = useRef<ButtonRefs>(null)

    useEffect(() => {
        const videoElement = videoRef.current
        if (reducedMotion) {
            videoElement.pause()
        }
    }, [reducedMotion])

    if (!url) {
        return null
    }

    if (viewportHelper.is.mobile) {
        url = urlMobile
    }

    const togglePlayPause = () => {
        const videoElement = videoRef.current

        if (isPlaying) {
            videoElement.pause()
        } else {
            videoElement.play()
        }

        setIsPlaying(!isPlaying)
    }

    return (
        <div className={`video-container ${className}`}>
            <video
                ref={videoRef}
                playsInline
                autoPlay={auto_play}
                muted
                loop
                aria-describedby="video-desc-external">
                <source src={url} type="video/mp4"></source>
                <track
                    src="captions_en.vtt"
                    kind="captions"
                    srcLang="en"
                    label="english_captions"
                />
            </video>

            <p
                aria-hidden="true"
                className="video-description"
                id="video-desc-external">
                {a11y_video_description}
            </p>

            {!hasModal && showControls ? (
                <button
                    id="autoplay-toggle"
                    className="video-play-button"
                    aria-pressed={isPlaying}
                    aria-label={isPlaying ? 'Pause video' : 'Play video'}
                    onClick={togglePlayPause}
                    data-testid={getTestId('video-autoplay-button')}>
                    <span>
                        {isPlaying ? (
                            <PauseIcon fillRect="white" />
                        ) : (
                            <PlayIcon fillRect="white" size={24} />
                        )}
                    </span>
                </button>
            ) : null}

            {hasModal ? (
                <span className="video-open-button">
                    <ExpandableButton
                        onClick={e => onVideoClick(e)}
                        ref={ctaRef}
                        {...button}
                    />
                </span>
            ) : null}
        </div>
    )
}

export default Video
